'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import {
  MdChevronRight,
  MdOutlineQrCode2,
  MdOutlineQrCodeScanner,
  MdOutlineBadge,
  MdOutlineLoyalty,
  MdOutlineConfirmationNumber,
  MdOutlineDashboard,
  MdOutlineLightMode,
  MdOutlineLogout,
} from 'react-icons/md';
import { Avatar } from '../../ui/avatar/Avatar';
import { AvatarVariant, AvatarSize } from '../../ui/avatar/Avatar.types';
import { BottomSheet } from '../../ui/bottom-sheet';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { cn } from '@/lib/styleUtils';
import {
  dropdownContainerClasses,
  userSectionClasses,
  userRowClasses,
  userNameClasses,
  userEmailClasses,
  actionCardsContainerClasses,
  actionCardClasses,
  actionCardTitleClasses,
  actionCardSubtitleClasses,
  actionCardIconClasses,
  actionCardTrailingIconClasses,
  menuSectionClasses,
  menuItemClasses,
  menuItemIconClasses,
  chevronClasses,
  overlayClasses,
} from './UserMenu.style';
import type { UserMenuProps } from './UserMenu.types';

export function UserMenu({
  open,
  userName,
  userEmail,
  onClose,
  onProfileClick,
  showAccessCardQr = false,
  onAccessCardQrClick,
  showLoyaltyQr = false,
  onLoyaltyQrClick,
  showScanTicket = false,
  onScanTicketClick,
  onOverviewClick,
  onChangeAppearanceClick,
  onLogoutClick,
  anchorRef,
  asBottomSheet = false,
}: UserMenuProps) {
  const t = useTranslations('UserMenu');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  useFocusTrap(dropdownRef, open && !asBottomSheet);

  // Position dropdown below the anchor, right-aligned (dropdown mode only — the bottom
  // sheet is full-width and needs no anchor math).
  useEffect(() => {
    if (asBottomSheet || !open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open, anchorRef, asBottomSheet]);

  // Escape key to close (dropdown mode; the BottomSheet handles its own Escape)
  useEffect(() => {
    if (!open || asBottomSheet) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, asBottomSheet]);

  // Shared menu body — identical content in both the dropdown and the bottom sheet.
  const menuBody = (
    <>
      {/* Actions: profile row + QR cards */}
      <div className={userSectionClasses}>
        {/* Profile row — 48px avatar, name + email, chevron */}
        <button
          type="button"
          className={userRowClasses}
          onClick={onProfileClick}
          role="menuitem"
          data-cy="user-menu-profile-button"
        >
          <Avatar
            name={userName}
            variant={AvatarVariant.accent}
            size={AvatarSize.large}
            className="tw:text-20 tw:shrink-0"
          />
          <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
            <span className={userNameClasses} data-cy="user-menu-user-name">
              {userName}
            </span>
            {userEmail && (
              <span className={userEmailClasses} data-cy="user-menu-user-email">
                {userEmail}
              </span>
            )}
          </div>
          <MdChevronRight className={chevronClasses} aria-hidden="true" />
        </button>

        {/* Action cards */}
        <div className={actionCardsContainerClasses}>
          {showAccessCardQr && (
            <button
              type="button"
              className={actionCardClasses}
              onClick={onAccessCardQrClick}
              role="menuitem"
              data-cy="user-menu-access-card-qr-button"
            >
              <MdOutlineBadge className={actionCardIconClasses} aria-hidden="true" />
              <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                <span className={actionCardTitleClasses}>{t('showAccessCardQr')}</span>
                <span className={actionCardSubtitleClasses}>{t('forPermitAccess')}</span>
              </div>
              <MdOutlineQrCode2 className={actionCardTrailingIconClasses} aria-hidden="true" />
            </button>
          )}
          {showLoyaltyQr && (
            <button
              type="button"
              className={actionCardClasses}
              onClick={onLoyaltyQrClick}
              role="menuitem"
              data-cy="user-menu-loyalty-qr-button"
            >
              <MdOutlineLoyalty className={actionCardIconClasses} aria-hidden="true" />
              <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                <span className={actionCardTitleClasses}>{t('showLoyaltyQr')}</span>
                <span className={actionCardSubtitleClasses}>{t('availDiscounts')}</span>
              </div>
              <MdOutlineQrCode2 className={actionCardTrailingIconClasses} aria-hidden="true" />
            </button>
          )}
          {showScanTicket && (
            <button
              type="button"
              className={actionCardClasses}
              onClick={onScanTicketClick}
              role="menuitem"
              data-cy="user-menu-scan-ticket-button"
            >
              <MdOutlineConfirmationNumber className={actionCardIconClasses} aria-hidden="true" />
              <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                <span className={actionCardTitleClasses}>{t('scanTicket')}</span>
                <span className={actionCardSubtitleClasses}>{t('manageParking')}</span>
              </div>
              <MdOutlineQrCodeScanner className={actionCardTrailingIconClasses} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Cells: menu items — leading icon + label, no trailing chevron */}
      <div className={menuSectionClasses}>
        <button
          type="button"
          className={menuItemClasses}
          onClick={onOverviewClick}
          role="menuitem"
          data-cy="user-menu-overview-button"
        >
          <MdOutlineDashboard className={menuItemIconClasses} aria-hidden="true" />
          <span>{t('overview')}</span>
        </button>
        <button
          type="button"
          className={menuItemClasses}
          onClick={onChangeAppearanceClick}
          role="menuitem"
          data-cy="user-menu-appearance-button"
        >
          <MdOutlineLightMode className={menuItemIconClasses} aria-hidden="true" />
          <span>{t('changeAppearance')}</span>
        </button>
        <button
          type="button"
          className={menuItemClasses}
          onClick={onLogoutClick}
          role="menuitem"
          data-cy="user-menu-logout-button"
        >
          <MdOutlineLogout className={menuItemIconClasses} aria-hidden="true" />
          <span>{t('logout')}</span>
        </button>
      </div>
    </>
  );

  if (!open) return null;

  // Bottom-sheet variant (mobile): full-width, no header — dismiss via backdrop / Escape.
  if (asBottomSheet) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        hideHeader
        title={null}
        ariaLabel={t('overview')}
        bodyClassName="tw:flex tw:flex-col tw:pt-base"
        data-cy="user-menu-sheet"
      >
        {menuBody}
      </BottomSheet>
    );
  }

  // Dropdown variant (desktop headers): positioned off the anchor.
  if (!position) return null;

  return createPortal(
    <>
      {/* Click-outside overlay */}
      <div
        className={overlayClasses}
        onClick={onClose}
        aria-hidden="true"
        data-cy="user-menu-overlay"
      />

      {/* Dropdown */}
      <div
        ref={dropdownRef}
        className={cn(dropdownContainerClasses, 'tw:fixed')}
        // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed dropdown position)
        style={{ top: position.top, right: position.right }}
        role="menu"
        aria-label={t('overview')}
        data-cy="user-menu"
      >
        {menuBody}
      </div>
    </>,
    document.body
  );
}
